// WCAG 2.x relative luminance and contrast helpers for concierge theming.
// Single source for Admin (advisory banner, preview) and Marketplace (ink
// selection at render). The server performs no contrast math, so there is no
// cross-language parity to maintain — these thresholds are client-only and
// advisory.

export type ConciergeScheme = 'DARK' | 'LIGHT';

// WCAG 1.4.11 non-text minimum — the floor for the accent against the canvas.
export const ACCENT_MIN_CONTRAST_RATIO = 3;

// 6-digit hex is the only stored theme color format — stricter than CSS on
// purpose, because these values are interpolated into style sheets and
// anything else must be dropped, never rendered.
const THEME_HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

export function isValidThemeHex(value: string | null | undefined): value is string {
  return typeof value === 'string' && THEME_HEX_PATTERN.test(value);
}

// Channel-level primitive for call sites whose colors arrive as computed
// rgb() component values (0–255) rather than stored hexes.
export function relativeLuminanceFromRgb([r, g, b]: [number, number, number]): number {
  const [lr, lg, lb] = [r, g, b].map((channel) => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return relativeLuminanceFromRgb([r, g, b]);
}

export function contrastRatioFromLuminances(luminanceA: number, luminanceB: number): number {
  const [hi, lo] = luminanceA > luminanceB ? [luminanceA, luminanceB] : [luminanceB, luminanceA];
  return (hi + 0.05) / (lo + 0.05);
}

export function contrastRatio(hexA: string, hexB: string): number {
  return contrastRatioFromLuminances(relativeLuminance(hexA), relativeLuminance(hexB));
}

export type ConciergeInk = 'BRAND' | 'MAX_CONTRAST';

export type ConciergeThemeResolution = {
  scheme: ConciergeScheme;
  ink: ConciergeInk;
};

// The three text inks, mirroring concierge-theme-css.ts. Scheme picks the
// derivation block; ink picks which text-primary that block declares.
export const CONCIERGE_INK_HEX = {
  DARK_BRAND: '#f8efd9', // --dcm-concierge-cream-100
  LIGHT_BRAND: '#485055',
  LIGHT_MAX_CONTRAST: '#0b1914', // --dcm-concierge-forest-950
} as const;

// WCAG AA for normal text. The concierge renders 12-16px body copy and no
// large text, so the 3:1 large-text allowance never applies here.
export const AA_BODY_CONTRAST_RATIO = 4.5;

// Take the highest-contrast ink; among inks clearing AA, prefer the brand
// ink. The two-step ordering matters: a plain "escalate below AA" rule would
// swap cream for forest-950 around L=0.16, where forest-950 is *worse*.
export function resolveConciergeTheme(hex: string): ConciergeThemeResolution {
  const canvas = relativeLuminance(hex);
  const cream = contrastRatioFromLuminances(canvas, relativeLuminance(CONCIERGE_INK_HEX.DARK_BRAND));
  const lightBrand = contrastRatioFromLuminances(canvas, relativeLuminance(CONCIERGE_INK_HEX.LIGHT_BRAND));
  const maxContrast = contrastRatioFromLuminances(canvas, relativeLuminance(CONCIERGE_INK_HEX.LIGHT_MAX_CONTRAST));

  if (cream >= AA_BODY_CONTRAST_RATIO) {
    return { scheme: 'DARK', ink: 'BRAND' };
  }

  if (lightBrand >= AA_BODY_CONTRAST_RATIO) {
    return { scheme: 'LIGHT', ink: 'BRAND' };
  }

  // This line is only reachable when both brand inks miss AA (cream and
  // lightBrand both < 4.5:1). In that reachable region the canvas luminance
  // is lighter than both inks, so the darker ink (maxContrast / forest-950)
  // always beats the lighter one (lightBrand / #485055) — comparing only
  // cream vs maxContrast is sufficient. The general claim "maxContrast
  // always dominates lightBrand" is false globally: for very dark canvases
  // (#485055 is lighter than the canvas) the #485055 ink wins. That case is
  // unreachable here because cream (#f8efd9) clears AA against any canvas
  // dark enough for #485055 to be the lighter ink, so this branch is never
  // entered for those canvases.
  return cream >= maxContrast ? { scheme: 'DARK', ink: 'BRAND' } : { scheme: 'LIGHT', ink: 'MAX_CONTRAST' };
}

function inkHexFor({ scheme, ink }: ConciergeThemeResolution): string {
  if (scheme === 'DARK') {
    return CONCIERGE_INK_HEX.DARK_BRAND;
  }

  return ink === 'MAX_CONTRAST' ? CONCIERGE_INK_HEX.LIGHT_MAX_CONTRAST : CONCIERGE_INK_HEX.LIGHT_BRAND;
}

// Contrast of the ink we will actually render against the canvas — the
// advisory's trigger, so the banner claims something true.
export function conciergeInkContrast(hex: string): number {
  return contrastRatio(inkHexFor(resolveConciergeTheme(hex)), hex);
}

export function isCanvasTextReadable(hex: string): boolean {
  return conciergeInkContrast(hex) >= AA_BODY_CONTRAST_RATIO;
}

// The accent must sit on the far side of the canvas — lighter than a dark
// canvas, darker than a light one — by at least 3:1. The label on accent
// fills needs no check here: resolveConciergeChromeForeground picks it from
// the accent alone, at >= ~4.58:1 for any accent.
export function isAccentReadable(accentHex: string, canvasHex: string): boolean {
  if (contrastRatio(accentHex, canvasHex) < ACCENT_MIN_CONTRAST_RATIO) {
    return false;
  }

  const accentIsLighter = relativeLuminance(accentHex) > relativeLuminance(canvasHex);
  return resolveConciergeTheme(canvasHex).scheme === 'DARK' ? accentIsLighter : !accentIsLighter;
}

const CHROME_FOREGROUND_LIGHT_HEX = '#ffffff';
const CHROME_FOREGROUND_DARK_HEX = '#000000';

/**
 * Contrast-safe ink for surfaces filled with the concierge accent. The
 * accent is never darkened (chrome and remote must agree on it), so the ink
 * flips instead: whichever of pure white/black contrasts higher. That is always
 * >= ~4.58:1, because the better of the two is lowest at relative luminance
 * ~0.179, where both measure ~4.58:1. Ties go to white.
 */
export function resolveConciergeChromeForeground(accentHex: string): string {
  const lightContrast = contrastRatio(accentHex, CHROME_FOREGROUND_LIGHT_HEX);
  const darkContrast = contrastRatio(accentHex, CHROME_FOREGROUND_DARK_HEX);
  return lightContrast >= darkContrast ? CHROME_FOREGROUND_LIGHT_HEX : CHROME_FOREGROUND_DARK_HEX;
}
